import '../main';
import * as z from 'zod';
import { _t, clearValidationErrors, currentLocale, displayValidationErrors, t } from '../../i18n';
import {
  Notifications,
  getI18nMonths,
  goToFormStep,
  inputLanguageFromLocale,
  isValidSelectDate,
  matchesMinAge,
  parseDefaultValue,
  setLoadingState,
  simpleIsoDateFromSelect,
  simulateFormAction,
} from '../helpers';
import { DATA_DEFAULT_ATTRIBUTE, EMAIL_PREFILL_LOCAL_STORAGE_KEY } from '../../../constants';
import { insertSpaceEveryThirdChar, phoneRegexPatternsForPrefixes } from '../helpers/phoneValidation';

const registerForm = document.querySelector<HTMLFormElement>('#formRegister');

/**
 * Handle the form submission.
 */
registerForm?.addEventListener('submit', async e => {
  // Get form data
  e.preventDefault();
  const formData = new FormData(e.target as HTMLFormElement, e.submitter);
  const formDataEntries = Object.fromEntries(formData) as unknown as RegisterIn;
  const formRef = e.target as HTMLFormElement;
  // Clear existing validation errors
  clearValidationErrors(formRef);

  // Our form has two steps. We need to know if the user clicked on the "save and continue" button or the "finish" button.
  const steps = Array.from(formRef.querySelectorAll<HTMLDivElement>('[data-step]'));
  const isFirstStep = steps.some(x => x.classList.contains('active') && x.getAttribute('data-step') === '1');

  try {
    if (isFirstStep) {
      const safeParse = await RegisterSchema.safeParseAsync(formDataEntries);
      if (safeParse.success) {
        /**
         * If the first step is valid, we can continue to the second step.
         */
        localStorage.removeItem(EMAIL_PREFILL_LOCAL_STORAGE_KEY);
        return goToFormStep(formRef, 2, 'input[name="password"]');
      } else {
        const issuePaths = safeParse.error.issues?.flatMap(issue => issue.path);
        const firstStepPaths = ['email', 'firstName', 'lastName'];
        if (!issuePaths.some(path => firstStepPaths.includes(String(path)))) {
          setLoadingState(formRef, false);
          /**
           * If the issues don't include any of the first step fields, we can continue to the second step.
           */
          return goToFormStep(formRef, 2, 'input[name="password"]');
        } else {
          setLoadingState(formRef, false);
          displayValidationErrors(formRef, safeParse.error, firstStepPaths);
        }
      }
    } else {
      setLoadingState(formRef, true);
      const parsed = await RegisterSchema.parseAsync(formDataEntries);

      const inputIn = new FormData();
      inputIn.append('email', parsed.email);
      if (parsed.password) {
        inputIn.append('password', parsed.password);
      }
      inputIn.append('first_name', parsed.firstName);
      inputIn.append('last_name', parsed.lastName);
      if (parsed.phone) {
        inputIn.append('phone_prefix', parsed.phoneNumberPrefix);
        inputIn.append('phone', parsed.phone);
      }
      if (parsed.favouritePlayer) {
        inputIn.append('favourite_player', parsed.favouritePlayer);
      }
      if (parsed.gender) {
        inputIn.append('gender', parsed.gender);
      }
      inputIn.append('language', inputLanguageFromLocale(currentLocale()));
      inputIn.append('birthday', simpleIsoDateFromSelect(parsed.day, parsed.month, parsed.year));

      if (parsed.marketing) {
        inputIn.append('marketing', 'on');
      }

      if (registerForm) {
        simulateFormAction(inputIn, registerForm);
      }
    }
  } catch (error) {
    if (error instanceof z.ZodError) {
      displayValidationErrors(formRef, error);
    } else {
      Notifications.createErrorToast(error, 'Register page form submit error:');
    }
    setLoadingState(formRef, false);
  }
});

/**
 * Handle the click on the "previous step" button.
 */
const prevStepButton = document.querySelector<HTMLAnchorElement>('.nav__previous');
prevStepButton?.addEventListener('click', e => {
  const formRef = document.querySelector<HTMLFormElement>('#formRegister');
  const firstFormStepWrapper = formRef?.querySelector<HTMLDivElement>('[data-step="1"]');

  // If we are not on the first step, go back to the first step.
  if (!firstFormStepWrapper?.classList.contains('active')) {
    e.preventDefault();

    if (formRef) {
      return goToFormStep(formRef, 1);
    }
  }
});

/**
 * Initial page load actions.
 */
document.addEventListener('DOMContentLoaded', () => {
  const emailInput = registerForm?.querySelector<HTMLInputElement>('input[name="email"]');
  const firstNameInput = registerForm?.querySelector<HTMLInputElement>('input[name="firstName"]');
  const lastNameInput = registerForm?.querySelector<HTMLInputElement>('input[name="lastName"]');
  const phonePrefixSelect = registerForm?.querySelector<HTMLSelectElement>('select[name="phoneNumberPrefix"]');
  const genderSelect = registerForm?.querySelector<HTMLSelectElement>('select[name="gender"]');

  /**
   * Prefill email input if it's in the local storage or passed to hbs.
   */
  if (emailInput) {
    const emailPrefillValue = localStorage.getItem(EMAIL_PREFILL_LOCAL_STORAGE_KEY);
    if (emailPrefillValue) {
      emailInput.value = emailPrefillValue;
    }

    const emailDefaultValue = parseDefaultValue(emailInput.getAttribute(DATA_DEFAULT_ATTRIBUTE));
    if (emailDefaultValue) {
      emailInput.value = emailDefaultValue;
    }

    // TESTING
    if (process.env.NODE_ENV === 'development') {
      emailInput.value = 'tomas.dvorak+12@elevup.com';
    }
  }

  /**
   * Prefill first name input if it's  passed to hbs.
   */
  if (firstNameInput) {
    const nameDefaultValue = parseDefaultValue(firstNameInput.getAttribute(DATA_DEFAULT_ATTRIBUTE));
    if (nameDefaultValue) {
      firstNameInput.value = nameDefaultValue;
    }

    // TESTING
    if (process.env.NODE_ENV === 'development') {
      firstNameInput.value = 'Tomas';
    }
  }

  /**
   * Prefill last name input if it's  passed to hbs.
   */
  if (lastNameInput) {
    const surnameDefaultValue = parseDefaultValue(lastNameInput.getAttribute(DATA_DEFAULT_ATTRIBUTE));
    if (surnameDefaultValue) {
      lastNameInput.value = surnameDefaultValue;
    }

    // TESTING
    if (process.env.NODE_ENV === 'development') {
      lastNameInput.value = 'Dvorak';
    }
  }

  /**
   * Prefill gender select if it's  passed to hbs.
   */
  if (genderSelect) {
    const genderDefaultValue = parseDefaultValue(genderSelect.getAttribute(DATA_DEFAULT_ATTRIBUTE));
    if (genderDefaultValue) {
      genderSelect.value = genderDefaultValue;
    }
  }

  if (phonePrefixSelect) {
    const prefixKeys = Object.keys(phoneRegexPatternsForPrefixes)
      .filter(prefix => prefix !== '+420' && prefix !== '+421')
      .sort((a, b) => {
        const numberA = Number(a.replace('+', ''));
        const numberB = Number(b.replace('+', ''));

        return numberA - numberB;
      });
    phonePrefixSelect.setAttribute('data-selected', 'true');
    const czSkOptGroup = document.createElement('optgroup');
    czSkOptGroup.label = 'Česko/Slovensko';
    czSkOptGroup.appendChild(new Option('+420', '+420', true));
    czSkOptGroup.appendChild(new Option('+421', '+421'));

    phonePrefixSelect.appendChild(czSkOptGroup);

    const othersOptGroup = document.createElement('optgroup');
    othersOptGroup.label = t('pages.common.others');
    othersOptGroup.setAttribute('data-i18n', 'pages.common.others');
    othersOptGroup.setAttribute('i18n-target', 'label');

    prefixKeys.forEach(prefix => {
      othersOptGroup.appendChild(new Option(prefix, prefix));
    });

    phonePrefixSelect.appendChild(othersOptGroup);
  }

  populateDatepickerSelects({ setDefault: true });
});

document.addEventListener('SpartaAuthLocaleChanged', () => {
  /**
   * We want to update month select on locale change.
   */
  populateMonthSelect(true);
});

function populateDatepickerSelects(options?: { setDefault?: boolean }) {
  const datepickerDefaultValue = registerForm
    ?.querySelector<HTMLDivElement>('.date-of-birth')
    ?.getAttribute(DATA_DEFAULT_ATTRIBUTE);
  const [defaultYear, defaultMonth, defaultDay] = (parseDefaultValue(datepickerDefaultValue)?.split('-') ?? []).map(x =>
    Number(x),
  );
  const daySelect = registerForm?.querySelector<HTMLSelectElement>('select[name="day"]');
  const yearSelect = registerForm?.querySelector<HTMLSelectElement>('select[name="year"]');

  /**
   * Populate the day select
   */
  if (daySelect) {
    const selectedIndex = daySelect?.selectedIndex;
    // Reset previous options
    daySelect.innerHTML = '<option selected value=""></option>';

    // Populate the day select
    for (let day = 1, i = 0; day <= 31; day++, i++) {
      daySelect?.options.add(new Option(String(day), String(day), selectedIndex === i));
    }

    if (options?.setDefault && defaultDay) {
      daySelect.value = String(defaultDay);
      daySelect.setAttribute('data-selected', 'true');
    }
  }

  /**
   * Populate the month select
   */
  populateMonthSelect(false, options?.setDefault ? defaultMonth : undefined);

  /**
   * Populate the year select
   */
  if (yearSelect) {
    const selectedIndex = yearSelect?.selectedIndex;
    const currentYear = new Date().getFullYear();
    const minYear = currentYear - 140;
    const maxYear = currentYear - 14;

    // Reset previous options
    yearSelect.innerHTML = '<option selected value=""></option>';

    // Populate the year select
    for (let year = maxYear, i = 0; year >= minYear; year--, i++) {
      yearSelect?.options.add(new Option(String(year), String(year), selectedIndex === i));
    }

    if (options?.setDefault && defaultYear) {
      yearSelect.value = String(defaultYear);
      yearSelect.setAttribute('data-selected', 'true');
    }
  }
}

/**
 * Populates the month select element with options.
 * @param preserveSelected - Whether to preserve the selected month.
 */
function populateMonthSelect(preserveSelected?: boolean, defaultMonth?: number) {
  const monthSelect = registerForm?.querySelector<HTMLSelectElement>('select[name="month"]');
  const selectedIndex = monthSelect?.selectedIndex;
  if (monthSelect) {
    // Reset previous options
    monthSelect.innerHTML = '<option selected value=""></option>';

    // Populate the month select
    getI18nMonths().forEach((monthItem, i) => {
      monthSelect?.options.add(
        new Option(monthItem.label, String(monthItem.monthIndex + 1), monthSelect.selectedIndex === i),
      );
    });

    // Preserve the selected month
    if (selectedIndex && preserveSelected) {
      monthSelect.selectedIndex = selectedIndex;
    }

    if (defaultMonth) {
      monthSelect.value = String(defaultMonth);
      monthSelect.setAttribute('data-selected', 'true');
    }
  }
}

const formGroupsWithBlurPlaceholder = document.querySelectorAll<HTMLDivElement>('.form-group[data-focused]');

Array.from(formGroupsWithBlurPlaceholder).forEach(formGroup => {
  const input = formGroup.querySelector<HTMLInputElement>('input');
  const blurPlaceholder = formGroup.querySelector<HTMLDivElement>('.blur-placeholder');
  formGroup.setAttribute('data-empty', String(!input?.value?.trim()));
  if (blurPlaceholder) {
    blurPlaceholder.textContent = insertSpaceEveryThirdChar(input?.value?.trim() ?? '');
  }

  input?.addEventListener('blur', e => {
    if (blurPlaceholder) {
      blurPlaceholder.textContent = insertSpaceEveryThirdChar(input?.value?.trim() ?? '');
    }

    formGroup.setAttribute('data-focused', 'false');
    formGroup.setAttribute('data-empty', String(!(e.target as HTMLInputElement)?.value?.trim()));
  });
  input?.addEventListener('focus', () => {
    formGroup.setAttribute('data-focused', 'true');
  });
});

const stringWithoutEmoji = z.string().regex(/^((?!\p{Emoji_Presentation}).)*$/u, _t('validations.emojiNotAllowed'));
const requiredStringWithoutEmoji = z
  .string({ required_error: _t('validations.required') })
  .regex(/^((?!\p{Emoji_Presentation}).)*$/u, _t('validations.emojiNotAllowed'));

/**
 * Validation schema for the register form.
 */
const emailSchema = z.string().email({ message: _t('validations.email') });
const RegisterPasswordSchema = z
  .object({
    sso_registration: z.string().optional(),
    password: stringWithoutEmoji.optional(),
    passwordAgain: stringWithoutEmoji.optional(),
  })
  .superRefine((val, ctx) => {
    // We need to check if user uses SSO register flow
    // and add password validations only if he doesn't
    if (!val.sso_registration || val.sso_registration === 'false' || val.sso_registration === '{{sso_registration}}') {
      if (!val.password) {
        ctx.addIssue({
          message: _t('validations.required'),
          path: ['password'],
          code: z.ZodIssueCode.custom,
          fatal: true,
        });

        return z.NEVER;
      }
      if (val.password) {
        if (val.password?.length < 6) {
          ctx.addIssue({
            message: _t('validations.minLength'),
            minimum: 6,
            path: ['password'],
            code: z.ZodIssueCode.too_small,
            type: 'string',
            inclusive: false,
            fatal: true,
          });

          return z.NEVER;
        }
        if (val.password?.length > 30) {
          ctx.addIssue({
            message: _t('validations.maxLength'),
            maximum: 30,
            path: ['password'],
            code: z.ZodIssueCode.too_big,
            type: 'string',
            inclusive: false,
            fatal: true,
          });
        }
      }
      if (!val.passwordAgain) {
        ctx.addIssue({
          message: _t('validations.required'),
          path: ['passwordAgain'],
          code: z.ZodIssueCode.custom,
          fatal: true,
        });

        return z.NEVER;
      }
      if (val.passwordAgain) {
        if (val.passwordAgain?.length < 6) {
          ctx.addIssue({
            message: _t('validations.minLength'),
            minimum: 6,
            path: ['passwordAgain'],
            code: z.ZodIssueCode.too_small,
            type: 'string',
            inclusive: false,
            fatal: true,
          });
        }
        if (val.passwordAgain?.length > 30) {
          ctx.addIssue({
            message: _t('validations.maxLength'),
            maximum: 30,
            path: ['passwordAgain'],
            code: z.ZodIssueCode.too_big,
            type: 'string',
            inclusive: false,
            fatal: true,
          });
        }
      }

      if (val.password !== val.passwordAgain) {
        ctx.addIssue({
          message: _t('validations.passwordsDontMatch'),
          path: ['passwordAgain'],
          code: z.ZodIssueCode.custom,
        });
      }

      if (val.password && val.passwordAgain) {
        if (!/[A-Z]/.test(val.password) || !/[a-z]/.test(val.password) || !/\d/.test(val.password)) {
          ctx.addIssue({
            message: _t('validations.passwordRequirements'),
            path: ['password'],
            code: z.ZodIssueCode.custom,
          });
        }

        return z.NEVER;
      }
    }
  });
// And we need to use intersection so the password refinements are triggered with base schema cycle
const RegisterSchema = z.intersection(
  z
    .object({
      email: emailSchema.refine(
        async email => {
          if (email && emailSchema.safeParse(email).success) {
            const response = await fetch(
              process.env.NODE_ENV === 'development'
                ? `https://auth.dev.sparta.cra.zone/v1/users/exists?email=${encodeURIComponent(email)}`
                : `/v1/users/exists?email=${encodeURIComponent(email)}`,
            );

            return response.status === 404;
          } else {
            return true;
          }
        },
        { message: _t('validations.emailIsUsed') },
      ),
      firstName: requiredStringWithoutEmoji
        .min(1, { message: _t('validations.required') })
        .max(50, { message: _t('validations.maxLength') }),
      lastName: requiredStringWithoutEmoji
        .min(1, { message: _t('validations.required') })
        .max(50, { message: _t('validations.maxLength') }),
      phoneNumberPrefix: z.string(),
      phone: z
        .union([
          z.string().length(0),
          z.string().min(4, { message: _t('validations.required') }),
          z.string().max(20, { message: _t('validations.maxLength') }),
        ])
        .optional()
        .transform(e => (e === '' ? undefined : e)),

      favouritePlayer: z.string().optional(),
      gender: z.string().optional(),
      day: z.string().min(1, { message: _t('validations.required') }),
      month: z.string().min(1, { message: _t('validations.required') }),
      year: z.string().min(1, { message: _t('validations.required') }),
      marketing: z
        .string()
        .transform(value => value === 'on')
        .optional(),
      terms: z.string({ required_error: _t('validations.required') }).transform(value => value === 'on'),
    })
    .refine(data => isValidSelectDate(data.day, data.month, data.year), {
      path: ['day'],
      message: _t('validations.invalidDate'),
    })
    .refine(data => matchesMinAge(data.day, data.month, data.year, 15), {
      path: ['day'],
      message: _t('validations.minAge'),
      params: {
        minimum: 15,
      },
    })
    .refine(data => isValidPhoneNumber(data.phoneNumberPrefix, data.phone), {
      path: ['phone'],
      message: _t('validations.invalidPhone'),
    }),
  RegisterPasswordSchema,
);

type RegisterIn = z.infer<typeof RegisterSchema>;

function isValidPhoneNumber(prefix: string, phoneNumber: string | undefined) {
  if (!phoneNumber) {
    return true;
  }

  let phone = phoneNumber;

  if (phone.startsWith(prefix)) {
    phone = phone.replace(prefix, '');
  }

  const match = phoneRegexPatternsForPrefixes[prefix];
  if (!match) {
    throw new Error(`Unhandled prefix: ${prefix}. Please add validation for this prefix.`);
  }

  const result = match.test(prefix + phone);

  return result;
}
